/**
 * Minimal Server-Sent Events reader over a fetch response body.
 *
 * The browser's EventSource cannot send an Authorization header, and the assistant authenticates with a
 * bearer token, so the stream is read with fetch and parsed here instead. Implements the parts of the
 * event-stream format (https://html.spec.whatwg.org/multipage/server-sent-events.html#parsing-an-event-stream)
 * a server can actually send: `event:` / `data:` / `id:` fields, multi-line data joined with "\n",
 * `:` comment lines (keep-alive pings), and CR, LF or CRLF line ends. `retry:` is ignored — reconnecting
 * is the caller's decision.
 */

export interface SseEvent {
  /** `message` when the server sent no `event:` field, as in EventSource. */
  event: string;
  data: string;
  id?: string;
}

/**
 * Reads events until the stream ends (resolves) or fails / is aborted (rejects) — including when
 * `onEvent` throws, which cancels the stream. `onEvent` is also called with nothing for comment lines,
 * so callers can treat keep-alive pings as a sign of life.
 */
export const readSseStream = async (body: ReadableStream<Uint8Array>, onEvent: (event: SseEvent | null) => void): Promise<void> => {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let eventName = '';
  let data: string[] = [];
  let id: string | undefined;

  const dispatch = () => {
    // A block with no data lines is not an event (the spec discards it).
    if (data.length > 0) onEvent({ event: eventName || 'message', data: data.join('\n'), id });
    eventName = '';
    data = [];
  };

  const processLine = (line: string) => {
    if (line === '') return dispatch();
    if (line.startsWith(':')) return onEvent(null);
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    // One optional space after the colon is part of the syntax, not the value.
    const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '');
    if (field === 'event') eventName = value;
    else if (field === 'data') data.push(value);
    else if (field === 'id') id = value;
  };

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // A CR at the very end may be the first half of a CRLF split across chunks: held back until the
      // next chunk says whether an LF follows, or it would read as two line ends — an empty line in
      // between, which dispatches the event half-way through.
      const heldCr = buffer.endsWith('\r');
      const lines = (heldCr ? buffer.slice(0, -1) : buffer).split(/\r\n|\r|\n/);
      // The last piece is an incomplete line; it waits for more bytes.
      buffer = (lines.pop() ?? '') + (heldCr ? '\r' : '');
      lines.forEach(processLine);
    }
    // Flush a final line without a line end; an event not closed by a blank line is still delivered.
    buffer = (buffer + decoder.decode()).replace(/\r$/, '');
    if (buffer) processLine(buffer);
    dispatch();
  } catch (error) {
    // Stopped mid-stream (a handler threw, or the read failed): close the connection rather than leave
    // it open until the server finishes.
    await reader.cancel().catch(() => undefined);
    throw error;
  } finally {
    reader.releaseLock();
  }
};
